import type { Tag } from '@/entities/tag'
import type { Company } from '@/entities/company'
import type { Asset } from './asset'
import type { ShelfFile } from './shelfFile'
import type { User } from './user'

export interface Deck {
  id: number
  title: string
  allow_internal_access: boolean
  archived: boolean
  company_id: number
  deck_image: string | null
  deck_order: number
  description: string | null
  file: ShelfFile | null
  file_id: number | null
  is_featured: boolean
  is_paid: boolean
  press_release_date: string | null
  press_release_headline: string | null
  show_3d_menu: boolean
  slides: Slide[]
  status: 'ready' | 'cloning'
  tags: readonly Tag[]
  thumbnail: string | null
  type_tags: DeckTypeTags[]
  url_slug: string | null
  view_access_type: 'private' | 'public' | 'selected'
  edit_access_type: 'private' | 'selected'
  created_at: string
  updated_at: string
  deleted_at: string | null
}

export interface IncludeCompany {
  company: Company
}

export interface IncludeAssets {
  assets: Asset[]
}

export interface SlideImage {
  id: number
  page_number: number
  url: string
  zoom_level: number
}

export interface SlideFile {
  id: number
  url: string
  name: string
  thumbnail: string
}

/**
 * SectionHeader = H
 * Image = I
 * PDF = P
 * Site = S
 * ThreeD = M
 * Dynamic = D
 * Meeting = E
 * Title = T
 * Video = V
 * Scene = W
 */
export type SlideType = 'H' | 'T' | 'I' | 'V' | 'P' | 'D' | 'S' | 'M' | 'E' | 'W'

export enum SlideTypes {
  Header = 'H',
  Image = 'I',
  Video = 'V',
  PDF = 'P',
  Dynamic = 'D',
  Site = 'S',
  Model = 'M',
  Meeting = 'E',
  Scene = 'W',
}

export interface ThreeDSlide {
  id: number
  title: string
  camera_id: number | null
  caption_time: number | null
  company_id: number | null
  content_url: string | null
  deck_id: number
  description: string | null
  file: SlideFile | null
  file_id?: number | null
  image: null
  model_camera?: { model_title: string; preview: string; title: string }
  model_id: number
  order: number
  page_number: null
  preview: null
  site_id: null
  site_image: null
  title_visible_in_3d_model: boolean
  type: 'M'
  url: string
  disabled?: boolean
}

export interface SiteSlide {
  id: number
  title: string
  camera_id: number
  caption_time: number | null
  company_id: number | null
  content_url: string | null
  deck_id: number
  description: string | null
  file: SlideFile | null
  file_id?: number | null
  image: SlideImage | null
  model_id: null
  order: number
  page_number: null
  preview: null
  site_image: string | null
  title_visible_in_3d_model: null
  type: 'S'
  url: string
  project_id: number
  site_id: number
  disabled?: boolean
}

export interface OtherSlide {
  id: number
  title: string
  camera_id: null
  caption_time: number | null
  company_id: number | null
  content_url: string | null
  deck_id: number
  description: string | null
  file: SlideFile | null
  file_id?: number | null
  image: SlideImage | null
  model_id: null
  order: number
  page_number: number | null
  preview: SlideImage | null
  site_image: null
  title_visible_in_3d_model: null
  type: Exclude<SlideType, 'M' | 'S'>
  url: string
  project_id: null
  site_id: null
  disabled?: boolean
}

export type NonThreeDSlide = SiteSlide | OtherSlide
export type Slide = ThreeDSlide | NonThreeDSlide

export interface IncludeSiteCamera {
  site_camera: {
    pan_x: number | string
    pan_y: number | string
    zoom: number
    hidden_hotspot_ids?: number[]
  }
}

export type DeckUserType = '' | 'host' | 'controlled'

export interface DeckUser extends User {
  deckUserType?: DeckUserType
}

/**
 * Define the type tags field for the deck model. These can be dynamically added through
 * various places in the application, so we need to make sure they are consistent.
 */
export enum DeckTypeTags {
  Investor = 'Investor',
  PressRelease = 'Press Release',
  SiteVisit = 'Virtual Site Visit',
  Technical = 'Technical',
  ESG = 'ESG',
}

export const LOCATION_DICTIONARY: Record<SlideType, string> = {
  S: 'site',
  I: 'image',
  P: 'pdf',
  M: 'model',
  D: 'dynamic',
  E: 'meeting',
  H: 'header',
  T: 'title',
  V: 'video',
  W: 'scene',
}
